/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Position } from 'geojson';
import type { Converter } from 'proj4';
import proj4 from 'proj4';

export type ProjectorCache = Record<string, Converter>;

// Cache for for storing and reusing Proj4 instances
export const projectorCache = {} as ProjectorCache;

// Ensure that you have a proj4 object, pulling from the cache if necessary
export const getProj4 = (projection: string | Converter): Converter => {
  // Duck typing: check for the 'forward' and 'inverse' methods which are present on proj4 projection objects
  if (
    typeof (projection as Converter).forward === 'function' &&
    typeof (projection as Converter).inverse === 'function'
  ) {
    return projection as Converter;
  }
  if (typeof projection === 'string' && projection in projectorCache) {
    return projectorCache[projection] as Converter;
  }
  projectorCache[projection as string] = proj4(projection as string);
  return projectorCache[projection as string] as Converter;
};

export const NEW_LINESTRING_CREATED = 'new point in LineString created';
export const NEW_FEATURE_CREATED = 'new feature created';
export const NEW_POINT_CREATED = 'new point created';

const roundToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const roundCoordinates = <
  T extends Position | Position[] | Position[][] | Position[][][],
>(
  coords: T,
): T => {
  if (!Array.isArray(coords)) {
    return coords;
  }

  const mapCoords = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(mapCoords);
    }
    if (typeof value === 'number') {
      return roundToDecimals(value, 5);
    }
    return value;
  };

  return mapCoords(coords) as T;
};
